import React, { useState } from 'react';
import styled from 'styled-components';
import { ArrowDown, ArrowUp, ImageOff, Trash2 } from 'lucide-react';
import { merchantExperienceApi, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { MerchantExperienceImageDto } from '@kardume/types';
import { ActionButton, Badge, EmptyState, ImageUpload, Thumbnail } from '@kardume/ui';
import { uploadMerchantImage } from '../../utils/imageUpload';

/**
 * A galeria da experiência (card #347).
 *
 * **A capa é a foto de menor `sortOrder`** - não existe campo de capa no
 * modelo, e inventar um aqui criaria uma segunda verdade sobre qual imagem o
 * catálogo mostra.
 *
 * ## Por que reordenar apaga e recria
 *
 * O core expõe só `POST /images` e `DELETE /images/{id}`: `sortOrder` é
 * definido na criação e não há rota que o altere. Mover uma foto, então, é
 * recriá-la na posição nova. Duas decisões seguem daí:
 *
 * - **Cria antes de apagar.** Se a sequência falhar no meio, o lojista fica com
 *   uma foto repetida - que ele remove em um clique - e não com uma foto
 *   perdida, que ele teria de reenviar.
 * - **Só as posições que mudaram são reescritas.** Numa troca entre vizinhas
 *   isso é duas fotos, não a galeria inteira.
 *
 * A alternativa honesta seria um `PATCH /images/{id}` no core; enquanto ele não
 * existe, este é o caminho que não perde imagem.
 */

const MAX_IMAGES = 12;

const Wrapper = styled.div`
  display: grid;
  gap: 1rem;
`;

const Intro = styled.p`
  margin: 0;
  font-size: 0.83rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.6rem;
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.6rem 0.7rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surface};
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: grid;
  gap: 0.3rem;

  .position {
    font-size: 0.78rem;
    color: ${(p) => p.theme.textSecondary};
  }
  .url {
    font-size: 0.7rem;
    color: ${(p) => p.theme.textMuted};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.4rem;
`;

const Uploader = styled.div`
  max-width: 320px;
`;

interface Props {
  experienceId: string;
  images: MerchantExperienceImageDto[];
  canManage: boolean;
  /** Recarrega o detalhe: a lista de fotos vem de lá. */
  onChanged: () => Promise<void> | void;
}

const ExperienceImagesPanel: React.FC<Props> = ({ experienceId, images, canManage, onChanged }) => {
  const { showNotification } = useNotification();
  const [busy, setBusy] = useState(false);

  const ordered = [...images].sort((a, b) => a.sortOrder - b.sortOrder);

  const addImage = async (url: string | undefined): Promise<void> => {
    if (!url) return;
    setBusy(true);
    try {
      const nextOrder = ordered.length === 0 ? 0 : ordered[ordered.length - 1].sortOrder + 1;
      await merchantExperienceApi.addImage(experienceId, { url, sortOrder: nextOrder });
      showNotification('Foto adicionada.', 'success');
      await onChanged();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível adicionar a foto.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeImage = async (image: MerchantExperienceImageDto): Promise<void> => {
    setBusy(true);
    try {
      await merchantExperienceApi.removeImage(experienceId, image.id);
      showNotification('Foto removida.', 'success');
      await onChanged();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível remover a foto.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  /** Troca a foto de posição com a vizinha (-1 acima, +1 abaixo). */
  const move = async (index: number, direction: -1 | 1): Promise<void> => {
    const target = index + direction;
    if (target < 0 || target >= ordered.length) return;

    const desired = [...ordered];
    [desired[index], desired[target]] = [desired[target], desired[index]];

    // Só o que saiu do lugar é reescrito.
    const changed = desired
      .map((image, position) => ({ image, position }))
      .filter(({ image, position }) => image.sortOrder !== position);
    if (changed.length === 0) return;

    setBusy(true);
    try {
      // Cria tudo antes de apagar qualquer coisa: falha no meio deixa foto
      // repetida, nunca foto perdida.
      for (const { image, position } of changed) {
        // eslint-disable-next-line no-await-in-loop
        await merchantExperienceApi.addImage(experienceId, { url: image.url, sortOrder: position });
      }
      for (const { image } of changed) {
        // eslint-disable-next-line no-await-in-loop
        await merchantExperienceApi.removeImage(experienceId, image.id);
      }
      await onChanged();
    } catch (err) {
      showNotification(
        extractErrorMessage(
          err,
          'Não foi possível reordenar as fotos. Confira a galeria - pode ter ficado uma foto repetida.'
        ),
        'error'
      );
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrapper>
      <Intro>
        A <strong>primeira foto é a capa</strong> - é ela que aparece na vitrine e na busca. Use as
        setas para trocar a ordem. Até {MAX_IMAGES} fotos.
      </Intro>

      {ordered.length === 0 ? (
        <EmptyState
          icon={<ImageOff size={28} />}
          title="Nenhuma foto ainda"
          description="Uma experiência sem foto compete na vitrine com outra que tem. A primeira que você enviar vira a capa."
        />
      ) : (
        <List>
          {ordered.map((image, index) => (
            <Item key={image.id}>
              <Thumbnail src={image.url} alt={`Foto ${index + 1} da experiência`} width={96} />
              <Info>
                <span className="position">
                  {index === 0 ? <Badge tone="info">Capa</Badge> : `Foto ${index + 1}`}
                </span>
                <span className="url" title={image.url}>
                  {image.url}
                </span>
              </Info>
              {canManage && (
                <Actions>
                  <ActionButton
                    aria-label="Mover para cima"
                    title={index === 0 ? 'Já é a capa' : 'Mover para cima'}
                    disabled={busy || index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp size={16} />
                  </ActionButton>
                  <ActionButton
                    aria-label="Mover para baixo"
                    title="Mover para baixo"
                    disabled={busy || index === ordered.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown size={16} />
                  </ActionButton>
                  <ActionButton
                    aria-label="Remover foto"
                    title="Remover"
                    color="#DC2626"
                    disabled={busy}
                    onClick={() => removeImage(image)}
                  >
                    <Trash2 size={16} />
                  </ActionButton>
                </Actions>
              )}
            </Item>
          ))}
        </List>
      )}

      {canManage && ordered.length < MAX_IMAGES && (
        <Uploader>
          <ImageUpload
            label="Adicionar foto"
            value={undefined}
            variant="cover"
            onUpload={uploadMerchantImage('GALLERY')}
            onChange={addImage}
          />
        </Uploader>
      )}
    </Wrapper>
  );
};

export default ExperienceImagesPanel;
